<template>
  <div class="w-full h-full">
    <div class="text-center">
      <div class="my-3">
        <LayupIcon class="my-3" />
        <h5 class="h5 text-grey-light-900 font-semibold text-lg">Create a LayUp account</h5>
        <div v-if="errorMessage" class="rounded-md w-full bg-red-100 px-4 py-3 text-sm text-red-700">
          {{ errorMessage }}
        </div>
      </div>
    </div>

    <form>
      <MobileNumberInput v-model="form.cellNumber" :label="'Mobile Number'" :disabled="isLoading" class="text-left" />

      <PasswordInput v-model="form.password" :disabled="isLoading" :label="`Password`" class="text-left" />

      <PasswordInput v-model="confirmPassword" :disabled="isLoading" :label="`Confirm Password`" class="text-left" />
      <TurnstileWidget ref="turnstileRef" @success="handleSuccess" />
      <LayupButton
        type="submit"
        :disabled="isLoading || !turnstileToken"
        data-testid="sign_up_button"
        :label="'Sign up'"
        :isLoading="isLoading"
        @click="onSubmit()"
      />

      <button
        type="button"
        class="w-full border rounded h-10 w-100 mt-3 disabled:opacity-50 hover:bg-gray-500 hover:text-white text-black"
        @click="goBack()"
        :disabled="isLoading"
      >
        Cancel
      </button>

      <div class="text-center mt-3 text-sm text-black">
        <p>
          Already have an account?
          <RouterLink :to="{ name: 'WidgetSignIn' }" class="btn text-brand-700 p-0 text-sm">Sign In</RouterLink>
        </p>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import LayupButton from '@/components/common/LayupButton.vue';
import MobileNumberInput from '@/components/common/MobileNumberInput.vue';
import PasswordInput from '@/components/common/PasswordInput.vue';
import TurnstileWidget from '@/components/common/TurnstileWidget.vue';
import LayupIcon from '@/components/icons/LayupIcon.vue';
import { NetworkError } from '@/errors';
import { getErrorMessage, signUp } from '@/services/auth';
import { useUserStore } from '@/stores/users';
import { isValidPassword, parseMobile } from '@/utils/form.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';

const userStore = useUserStore();
const router = useRouter();

const errorMessage = ref('');
const isLoading = ref(false);
const turnstileToken = ref('');
const turnstileRef = ref<InstanceType<typeof TurnstileWidget> | null>(null);

const form = {
  cellNumber: '',
  password: '',
};

const confirmPassword = ref('');

const onSubmit = async () => {
  try {
    errorMessage.value = '';

    const passwordValidation = isValidPassword(form.password, confirmPassword.value);
    if (!passwordValidation.isValid) {
      errorMessage.value = passwordValidation.errorMessage;
    } else {
      isLoading.value = true;
      form.cellNumber = parseMobile(form.cellNumber);
      const response = await signUp(form.cellNumber, form.password, turnstileToken.value);
      addMetric('sign-up', { status: 'success', channel: 'widget' });
      userStore.user = {
        cellNumber: response.cellNumber,
        _id: response._id,
        name: response.name,
        verifiedEmail: response.verifiedEmail,
      };
      if (response.cellNumber) {
        await router.replace({ name: 'OtpVerification' });
      }
    }
  } catch (error: any) {
    turnstileRef.value?.reset();
    turnstileToken.value = '';
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('sign-up', { status: 'network_error', channel: 'widget' });
      errorMessage.value = 'Network Error. Please try again.';
    } else {
      createException(error);
      addMetric('sign-up', { status: 'error', channel: 'widget' });
      errorMessage.value = getErrorMessage(error);
    }
    isLoading.value = false;
  }
};

const handleSuccess = (token: string) => {
  turnstileToken.value = token;
};

onUnmounted(() => {
  isLoading.value = false;
});

const goBack = () => {
  router.back();
};
</script>
